'use client'

import { addMonths, format } from 'date-fns'
import { MessageCircle, Phone } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useDispatch } from 'react-redux'
import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import FormLead from '@/components/FormLead'
import { CONTACT_PHONE_HREF } from '@/lib/public-config'
import type { RentToBuyDetailContent } from '@/lib/rent-to-buy-detail-content'
import { parseRentalQuote, parseVehiclePriceExcludingVat, type RentalQuote } from '@/lib/rent-to-buy-quote'
import { addVehicleToCart, clearCart } from '@/redux/cartSlice'

const annualKilometres = [10000, 15000, 20000, 30000, 40000]
const durations = [12, 24, 36, 48]
type RentalService = {
  codice_nclad: string
  cprownum: number
  descrizione_nclad: string
  importo: string
  obbligatorio_nclad: number
}
const numericAmount = (value: string) => Number(value.replace(',', '.') || 0)

const formatCurrencyNoDecimals = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  useGrouping: 'always',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const formatInputAmount = (value: string) =>
    value === '' ? '' : new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2, useGrouping: 'always' }).format(numericAmount(value))

const normalizeAmountInput = (value: string): string | null => {
  const trimmed = value.replace(/\s/g, '')

  // Accetta anche importi italiani incollati, ad esempio "3.000,50".
  if (/^\d{1,3}(?:\.\d{3})+(?:,\d{0,2})?$/.test(trimmed)) {
    return trimmed.replace(/\./g, '').replace(',', '.')
  }

  if (trimmed === '' || /^\d+(?:[.,]\d{0,2})?$/.test(trimmed)) {
    return trimmed.replace(',', '.')
  }

  return null
}

interface Props {
  veicoloData: any
  tipoUtenteListino: string
  content: RentToBuyDetailContent
  onChangeKm: (value: string) => void
  onChangeDurata: (value: string) => void
  onChangeAnticipo: (value: string) => void
  onOpenQuote: () => void
  onSelectedServicesChange: (services: RentalService[]) => void
  onAvailableServicesChange: (services: RentalService[]) => void
  onDepositChange: (value: string) => void
}

export default function RentToBuyCalculator({
                                              veicoloData,
                                              tipoUtenteListino,
                                              content,
                                              onChangeKm,
                                              onChangeDurata,
                                              onChangeAnticipo,
                                              onOpenQuote,
                                              onSelectedServicesChange,
                                              onAvailableServicesChange,
                                              onDepositChange,
                                            }: Props) {
  const [km, setKm] = useState(String(content.initialAnnualKm))
  const [durata, setDurata] = useState(String(content.initialDurationMonths))
  const [anticipo, setAnticipo] = useState(String(content.initialDownPayment))
  const [cauzione, setCauzione] = useState(String(content.initialDeposit))
  const [focusedAmount, setFocusedAmount] = useState<'anticipo' | 'cauzione' | null>(null)
  const [quote, setQuote] = useState<RentalQuote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [amountLimitError, setAmountLimitError] = useState('')
  const [services, setServices] = useState<RentalService[]>([])
  const [selectedServiceCodes, setSelectedServiceCodes] = useState<number[]>([])
  const [servicesError, setServicesError] = useState('')
  const [expandedServiceCode, setExpandedServiceCode] = useState<number | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [financeCalloutOpen, setFinanceCalloutOpen] = useState(false)
  const netVehiclePrice = parseVehiclePriceExcludingVat(veicoloData.prezzoSenzaIva)
  const dispatch = useDispatch()
  const router = useRouter()

  useEffect(() => {
    const idArticolo = veicoloData.articolo || veicoloData._id || veicoloData.id
    if (!idArticolo) return
    const today = new Date()
    const query = new URLSearchParams({
      dataInizio: format(today, 'yyyy-MM-dd'),
      dataFine: format(addMonths(today, Number(durata)), 'yyyy-MM-dd'),
      categoria: km === '40000' ? 'SUPER1' : km,
    })
    const controller = new AbortController()
    setServices([])
    setSelectedServiceCodes([])
    setServicesError('')
    fetch(`/api/noleggio/servizi/${encodeURIComponent(idArticolo)}?${query}`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error('Impossibile caricare i servizi.')
        return response.json()
      })
      .then(data => {
        const available = Array.isArray(data) ? data : data.servizi || []
        setServices(available)
        onAvailableServicesChange(available)
      })
      .catch(cause => { if (!controller.signal.aborted) setServicesError(cause instanceof Error ? cause.message : 'Impossibile caricare i servizi.') })
    return () => controller.abort()
  }, [veicoloData.articolo, veicoloData._id, veicoloData.id, durata, km])

  useEffect(() => {
    onSelectedServicesChange(services.filter(service => service.obbligatorio_nclad !== 1 && selectedServiceCodes.includes(service.cprownum)))
  }, [services, selectedServiceCodes, onSelectedServicesChange])

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      const amountLimit = anticipo === '' ? null : quote?.downPaymentMaximum
      const depositLimit = cauzione === '' ? null : quote?.depositMaximum
      if ((amountLimit !== null && amountLimit !== undefined && numericAmount(anticipo) > amountLimit) || (depositLimit !== null && depositLimit !== undefined && numericAmount(cauzione) > depositLimit)) {
        const isAdvanceOverLimit = amountLimit !== null && amountLimit !== undefined && numericAmount(anticipo) > amountLimit
        const field = isAdvanceOverLimit ? 'anticipo' : 'cauzione'
        const limit = isAdvanceOverLimit ? amountLimit : depositLimit
        setAmountLimitError(`Il valore massimo ${isAdvanceOverLimit ? "dell'anticipo" : 'della cauzione'} è ${formatCurrencyNoDecimals.format(limit!)}`)
        setLoading(false)
        return
      }
      setAmountLimitError('')
      const article = veicoloData.articolo2
      const apiBase = process.env.NEXT_PUBLIC_API_URL
      if (!article || !apiBase) {
        setLoading(false)
        setError('Il calcolo non è disponibile per questo veicolo.')
        return
      }

      const today = new Date()
      const durationMonths = Number(durata)
      try {
        const calculationParams = {
            comando: 'CALNOL',
            articolo: article,
            category: km === '40000' ? 'SUPER1' : km,
            duration: durata,
            cauzione: String(numericAmount(cauzione)),
            dataInizio: format(today, 'yyyy-MM-dd'),
            dataFine: format(addMonths(today, durationMonths), 'yyyy-MM-dd'),
            selectedServices: services.filter(service => service.obbligatorio_nclad !== 1 && selectedServiceCodes.includes(service.cprownum)),
        }
        const requestCalculation = async (acconto: string) => {
          const response = await fetch(`${apiBase}/api/noleggio`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({ ...calculationParams, acconto }),
          })
          if (!response.ok) throw new Error('Rental calculation failed')
          return parseRentalQuote(await response.json())
        }
        const parsed = await requestCalculation(String(numericAmount(anticipo)))
        if (!parsed) throw new Error('Unexpected rental calculation response')
        setQuote(parsed)
        setError('')
      } catch (cause) {
        if (controller.signal.aborted) return
        setQuote(null)
        setError('Non è stato possibile calcolare la rata. Riprova più tardi.')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, 350)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [veicoloData.articolo2, km, durata, anticipo, cauzione, selectedServiceCodes, services])

  const selectKm = (value: number) => {
    const next = String(value)
    setLoading(true)
    setKm(next)
    onChangeKm(next)
  }

  const selectDuration = (value: number) => {
    const next = String(value)
    setLoading(true)
    setDurata(next)
    onChangeDurata(next)
  }

  const changeAmount = (value: string, field: 'anticipo' | 'cauzione') => {
    const normalized = normalizeAmountInput(value)
    if (normalized === null) return

    setLoading(true)
    if (field === 'anticipo') {
      setAnticipo(normalized)
      onChangeAnticipo(normalized)
    } else {
      setCauzione(normalized)
      onDepositChange(normalized)
    }
  }

  const addToCart = () => {
    if (!quote) return
    dispatch(clearCart())
    dispatch(addVehicleToCart({
      ...veicoloData,
      type: 'noleggio',
      kmNoleggio: km,
      durataNoleggio: durata,
      anticipoNoleggio: anticipo,
      cauzioneNoleggio: cauzione,
      rataNoleggio: String(quote.monthlyRate),
    }))
    router.push('/checkout')
  }

  return (
      <section className="rounded-[20px] border border-[#dce4ee] bg-white p-5 shadow-[0_20px_24px_rgba(5,15,51,0.1)] lg:rounded-[28px] lg:p-8" aria-label={content.calculatorTitle}>
        {chatOpen && (
            <FormLead
                open={chatOpen}
                title={content.chatButtonLabel}
                marca={veicoloData.marca}
                modello={veicoloData.modello}
                articolo={veicoloData.articolo2}
                setOpen={setChatOpen}
                textarea={true}
                whatsappMessage={`Salve, sono interessato al Rent to Buy del veicolo ${veicoloData.marca ?? ''} ${veicoloData.modello ?? ''} (${veicoloData.articolo2 ?? ''}).`}
            />
        )}

        <div className="space-y-5 lg:space-y-6">
          {netVehiclePrice !== null && (
              <div className="border-b border-[#dce4ee] pb-6">
                <p className="text-xs font-extrabold tracking-[0.08em] text-[#4a586f]">{content.vehiclePriceLabel}</p>
                <div className="mt-1 flex flex-wrap items-baseline gap-2.5">
                  <strong className="text-4xl font-extrabold tracking-tight text-[#10223a] lg:text-[44px]">{formatCurrencyNoDecimals.format(netVehiclePrice)}</strong>
                  <span className="text-[15px] font-semibold text-[#4a586f]">{content.priceVatSuffix}</span>
                </div>
              </div>
          )}
          <div>
            <h2 className="text-xl font-extrabold leading-tight lg:text-[22px]">{content.calculatorTitle}</h2>
            <p className="mt-1 text-sm font-medium leading-relaxed text-[#4a586f]">{content.calculatorDescription}</p>
          </div>

          <fieldset>
            <legend className="mb-2.5 text-sm font-extrabold">{content.annualKmLabel}</legend>
            <div className="grid grid-cols-5 gap-1.5 lg:gap-2">
              {annualKilometres.map((value) => (
                  <button key={value} type="button" aria-pressed={km === String(value)} onClick={() => selectKm(value)}
                          className={`min-w-0 rounded-xl border-[1.5px] px-0.5 py-3.5 text-xs font-extrabold sm:text-[13px] lg:text-[15px] ${km === String(value) ? 'border-[#07137e] bg-[#07137e] text-white' : 'border-[#dce4ee] bg-white text-[#10223a]'}`}>
                    {new Intl.NumberFormat('it-IT').format(value)}
                  </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-2.5 text-sm font-extrabold">{content.durationLabel}</legend>
            <div className="grid grid-cols-4 gap-1.5 lg:gap-2">
              {durations.map((value) => (
                  <button key={value} type="button" aria-pressed={durata === String(value)} onClick={() => selectDuration(value)}
                          className={`rounded-xl border-[1.5px] px-1 py-3.5 text-[13px] font-extrabold sm:text-[15px] ${durata === String(value) ? 'border-[#07137e] bg-[#07137e] text-white' : 'border-[#dce4ee] bg-white text-[#10223a]'}`}>
                    {value} mesi
                  </button>
              ))}
            </div>
          </fieldset>

          {(services.length > 0 || servicesError) && (
            <fieldset className="space-y-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <legend className="text-sm font-extrabold">Servizi Opzionali</legend>
                <span className="text-xs text-[#4a586f]">Aggiungi quelli che vuoi</span>
              </div>
              {servicesError && <p role="alert" className="text-sm text-red-700">{servicesError}</p>}
              {services.filter(service => service.obbligatorio_nclad !== 1).map(service => {
                const selected = selectedServiceCodes.includes(service.cprownum)
                const expanded = expandedServiceCode === service.cprownum
                const editorial = content.rentalAccessories.find(item => item.cprownum === service.cprownum)
                const description = editorial?.description || service.descrizione_nclad
                return <div key={service.cprownum} className={`rounded-xl border-[1.5px] px-3 py-3 ${selected ? 'border-[#07137e] bg-[#07137e]/[0.03]' : 'border-[#dce4ee] bg-white'}`}>
                  <div className="flex items-start gap-2.5">
                    <input aria-label={`Seleziona ${service.descrizione_nclad}`} type="checkbox" checked={selected}
                      onChange={() => setSelectedServiceCodes(current => current.includes(service.cprownum) ? current.filter(code => code !== service.cprownum) : [...current, service.cprownum])}
                      className="mt-0.5 size-[18px] shrink-0 accent-[#07137e]" />
                    <p className="min-w-0 flex-1 text-[13px] font-bold leading-[1.35] text-[#10223a]">{description}</p>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <strong className="whitespace-nowrap text-[13px] text-[#10223a]">+ {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(service.importo))}</strong>
                      {editorial?.details && <button type="button" aria-expanded={expanded} onClick={() => setExpandedServiceCode(expanded ? null : service.cprownum)} className="text-xs font-bold text-[#07137e]">{expanded ? 'Nascondi dettagli' : 'Dettagli'}</button>}
                    </div>
                  </div>
                  {expanded && editorial?.details && <p className="mt-2 pl-[28px] text-xs leading-relaxed text-[#4a586f]">{editorial.details}</p>}
                </div>
              })}
            </fieldset>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
          {([
            { id: 'anticipo', label: content.downPaymentLabel, value: anticipo },
            { id: 'cauzione', label: content.depositLabel, value: cauzione },
          ] as const).map((field) => (
              <div key={field.id}>
                <label htmlFor={`rental-${field.id}`} className="mb-2.5 block text-sm font-extrabold">{field.label}</label>
                <div className="flex items-center rounded-xl border-[1.5px] border-[#dce4ee] px-4 py-3.5">
                  <input
                      id={`rental-${field.id}`}
                      inputMode="decimal"
                      value={focusedAmount === field.id ? field.value.replace('.', ',') : formatInputAmount(field.value)}
                      onFocus={() => setFocusedAmount(field.id)}
                      onBlur={() => setFocusedAmount(null)}
                      onChange={(event) => changeAmount(event.target.value, field.id)}
                      className="w-full min-w-0 bg-transparent text-base font-bold outline-none"
                  />
                  <span className="text-sm font-semibold text-[#4a586f]">€</span>
                </div>
              </div>
          ))}
          </div>
          {amountLimitError && <p role="alert" className="text-sm text-red-700">{amountLimitError}</p>}

          <div className={`w-full rounded-xl border border-[#c7d1f2] bg-[#eff2fc] px-3.5 py-3 ${financeCalloutOpen ? 'space-y-2.5' : ''}`}>
            <button type="button" aria-expanded={financeCalloutOpen} onClick={() => setFinanceCalloutOpen(open => !open)} className="flex w-full items-center justify-between gap-3 text-left">
              <span className="min-w-0 flex-1 text-[13px] font-extrabold leading-[1.3] text-[#10223a]">{content.financeCostCalloutTitle}</span>
              <span className="flex shrink-0 items-center gap-1 text-xs font-bold text-[#07137e]">
                {financeCalloutOpen ? 'Chiudi' : content.financeCostCalloutLinkLabel}
                {financeCalloutOpen ? <ChevronUp size={12} aria-hidden="true" /> : <ChevronDown size={12} aria-hidden="true" />}
              </span>
            </button>
            {financeCalloutOpen && <div className="space-y-1.5 text-[13px] leading-[1.45] text-[#4a586f]">
              <p>{content.financeCostCalloutIntro}</p>
              <p className="flex gap-2"><span aria-hidden="true" className="mt-[7px] size-1 shrink-0 rounded-full bg-[#07137e]" /><span><strong className="text-[#10223a]">{content.financeCostCalloutAdvanceLabel}</strong> {content.financeCostCalloutAdvanceText}</span></p>
              <p className="flex gap-2"><span aria-hidden="true" className="mt-[7px] size-1 shrink-0 rounded-full bg-[#07137e]" /><span><strong className="text-[#10223a]">{content.financeCostCalloutDepositLabel}</strong> {content.financeCostCalloutDepositText}</span></p>
            </div>}
          </div>

          <div className="rounded-[20px] bg-gradient-to-br from-[#07137e] to-[#005aa9] p-5 text-white lg:p-6" aria-live="polite">
            <p className="text-xs font-extrabold tracking-[0.08em] text-white/75">{content.monthlyRateLabel}</p>
            <p className="mt-1 text-[44px] font-extrabold tracking-tight lg:text-[54px]">
              {loading ? '…' : quote ? formatCurrencyNoDecimals.format(quote.monthlyRate) : '—'}
              <span className="ml-1 text-lg font-bold text-white/85">/mese</span>
            </p>
            <p className="mt-1 text-xs font-semibold text-white/75">
              IVA esclusa · {durata} mesi · anticipo {formatCurrencyNoDecimals.format(numericAmount(anticipo))} · cauzione {formatCurrencyNoDecimals.format(numericAmount(cauzione))}
            </p>
            {quote?.breakdown && !loading && (
                <div className="mt-4 space-y-3 border-t border-white/20 pt-4 text-sm">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2">
                    <span className="min-w-0 font-semibold text-white/85">{content.recoveredAmountLabel}</span>
                    <strong className="whitespace-nowrap text-base">{formatCurrencyNoDecimals.format(quote.breakdown.recoveredAmount)}</strong>
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2">
                    <span className="min-w-0 font-semibold text-white/85">{content.servicesAndFinanceLabel}</span>
                    <strong className="whitespace-nowrap text-base">{formatCurrencyNoDecimals.format(quote.breakdown.servicesAndFinance)}</strong>
                  </div>
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2">
                    <span className="min-w-0 font-semibold text-white/85">{content.finalResidualLabel}</span>
                    <strong className="whitespace-nowrap text-base">{formatCurrencyNoDecimals.format(quote.breakdown.finalResidual)}</strong>
                  </div>
                </div>
            )}
          </div>

          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          {!loading && quote && !quote.breakdown && <p className="text-xs font-medium text-[#4a586f]">{content.breakdownUnavailable}</p>}
          <p className="text-xs font-medium leading-relaxed text-[#4a586f]">{content.quoteNote}</p>
          <button type="button" onClick={onOpenQuote}
                  className="flex w-full items-center justify-center gap-2.5 rounded-[14px] bg-[#d20000] px-6 py-5 text-base font-extrabold text-white hover:bg-[#b80000]">
            <Image src="/assets/rent-to-buy/icon-download.svg" width={20} height={20} alt="" aria-hidden="true" /> {content.quoteButtonLabel}
          </button>
          <div className="grid grid-cols-2 gap-2.5">
            <button type="button" onClick={() => setChatOpen(true)} className="flex items-center justify-center gap-2 rounded-xl border-[1.5px] border-[#dce4ee] py-3.5 text-sm font-bold">
              <MessageCircle size={18} aria-hidden="true" /> {content.chatButtonLabel}
            </button>
            <a href={CONTACT_PHONE_HREF} className="flex items-center justify-center gap-2 rounded-xl border-[1.5px] border-[#dce4ee] py-3.5 text-sm font-bold">
              <Phone size={18} aria-hidden="true" /> {content.callButtonLabel}
            </a>
          </div>
          {tipoUtenteListino === 'COM' && (
              <button type="button" onClick={addToCart} disabled={!quote || loading}
                      className="w-full rounded-xl border-2 border-[#005aa9] px-5 py-3.5 font-bold text-[#005aa9] disabled:opacity-50">
                Aggiungi al carrello
              </button>
          )}
        </div>
      </section>
  )
}
